import React, { forwardRef, useEffect, useState } from "react";
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { CREATIVE_NAMES, CABOODLE_CREATIVE_AD_FIELDS } from "./definitions";
import AssetInputOrUploadSection from "./AssetInputOrUploadSection";
import BaseCreativeControls from "./BaseCreativeControls";
import { InteractGrid } from "./functions";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import InputTextSection from "./InputTextSection";

const CREATIVE_TYPE = CREATIVE_NAMES.CABOODLE_CREATIVE

const CaboodleCreative = forwardRef(function CaboodleCreative({
    banner,
    handleChange,
    currentCustomer,
    impressionTag,
    clickTag,
    adServingType,
    data,
    container,
    isEditMode = false,
}, ref) {
    /** @type {[InteractGrid | null, React.Dispatch<React.SetStateAction<InteractGrid>>]} */
    const [grid, setGrid] = useState(null)
    /** @type {[{id: string, el: HTMLElement, index: number, url: string}[], React.Dispatch<React.SetStateAction<{id: string, el: HTMLElement}>>]} */
    const [selectedAreas, setSelectedAreas] = useState([])

    useEffect(() => {
        try {
            if (banner && ref) {
                ref.current = { ...ref.current, grid }
            } else {
                if (grid) {
                    grid.destroy()
                }
                setSelectedAreas([])
                initilizeGrid()
            }
        } catch {
            // to-do: implement error handling messages and possibly error-recovery behavior here
        }
    }, [banner])

    useEffect(() => {
        if (Array.isArray(data) && data.length) {
            if (isEditMode && grid) {
                /** @type {{x1: number, y1: number, x2: number, y2: number, url: string}[]} */
                let coordsData = data
                /** @type {InteractGrid} */
                let gridInstance = grid

                coordsData.forEach(o => {
                    let { x1, y2, x2, y1, url } = o
                    gridInstance.addBlock(
                        x1,
                        y2,
                        Math.abs(x1 - x2),
                        Math.abs(y1 - y2),
                        null,
                        url
                    )
                })
                setSelectedAreas(
                    gridInstance.draggableBlocks.map((o, idx) => {
                        return { id: o.name, el: o.el, index: o.index, url: data[idx]?.url || '' }
                    })
                )
            }
        }
    }, [data, isEditMode, grid])

    useEffect(initilizeGrid, [])

    function initilizeGrid() {
        let instance = new InteractGrid(document.querySelector('.draggable-container'))
        setGrid(instance)
    }

    function addSelectionArea() {
        if (grid) {
            grid.addBlock(0, 0, 80, 80, (id, el, index) => {
                setSelectedAreas([...selectedAreas, { id, el, index, url: '' }])
            })
        }
    }

    function removeSelectionArea(index) {
        if (grid) {
            setSelectedAreas(selectedAreas.filter((_, idx) => {
                return idx !== index
            }))
            grid.removeBlock(index)
        }
    }

    /**
     * 
     * @param {string} id 
     * @param {HTMLElement} el 
     */
    function focusSelectionArea(el) {
        el.classList.add('highlighted')
    }

    /**
     * 
     * @param {string} id 
     * @param {HTMLElement} el 
     */
    function blurSelectionArea(el) {
        el.classList.remove('highlighted')
    }

    /**
     * 
     * @param {number} idx 
     * @param {string} value 
     */
    function handleInputChange(idx, value) {
        setSelectedAreas(
            selectedAreas.map((gridData, index) => {
                if (index === idx) {
                    grid.draggableBlocks[index].url = value
                    try {
                        ref.current.data[idx] = value
                    } catch (err) {
                        console.log({ err })
                    }
                    return { ...gridData, url: value }
                } else {
                    return gridData
                }
            })
        )
    }

    return (
        <>
            <BaseCreativeControls
                changeHandler={handleChange}
                clickTag={clickTag}
                creativeType={CREATIVE_TYPE}
                impressionTag={impressionTag}
                adServingType={adServingType}
            >
                <AssetInputOrUploadSection
                    inputValue={banner}
                    inputLabel="Banner"
                    inputName={CABOODLE_CREATIVE_AD_FIELDS.banner}
                    inputChangeHandler={handleChange}
                    inputAdtype={CREATIVE_TYPE}
                    isRequired={true}
                    currentCustomer={currentCustomer}
                    accept="image/*"
                    defaultExtension="jpeg"
                />
                <InputTextSection
                    label="Container"
                    name={CABOODLE_CREATIVE_AD_FIELDS.container}
                    value={container}
                    changeHandler={handleChange}
                    adtype={CREATIVE_TYPE}
                    required={true}
                />

                {
                    selectedAreas.map((obj, index) => {
                        return (
                            <div
                                key={obj.id}
                                css={css`
                                    position: relative;
                                    display: flex;
                                    flex-wrap: wrap;
                                    flex-direction: row;
                                    margin-bottom: 10px;
                                    align-items: center;
                                    label {
                                        flex-basis: 90%;
                                    }
                                    button {
                                        flex-basis: 10%;
                                    }
                                    input {
                                        width: 100%;
                                    }
                                `}>
                                <label css={css`
                                        font-weight: 600;
                                        margin-bottom: 5px;
                                        position: relative;
                                    `}>
                                    Selection area #{index + 1}
                                    <InputText
                                        value={obj.url}
                                        onChange={ev => {
                                            handleInputChange(index, ev.target.value)
                                        }}
                                        key={obj.id}
                                        onMouseOver={() => {
                                            focusSelectionArea(obj.el)
                                        }}
                                        onMouseOut={() => {
                                            blurSelectionArea(obj.el)
                                        }}
                                        onFocus={ev => {
                                            focusSelectionArea(obj.el)
                                        }}
                                        onBlur={ev => {
                                            blurSelectionArea(obj.el)
                                        }}
                                    />
                                </label>
                                <Button
                                    className="rem-btn p-button-danger p-button-rounded p-button-text p-button-icon-only"
                                    icon="pi pi-times"
                                    onClick={() => {
                                        removeSelectionArea(index)
                                    }}
                                />
                            </div>
                        )
                    })
                }

                <div className="flex flex-end">
                    <Button
                        className="p-button p-buton-icon p-button-outlined mb-1"
                        icon="pi pi-plus"
                        label="Add clickable area"
                        onClick={addSelectionArea}
                    />
                </div>
            </BaseCreativeControls>
        </>
    );
})

export default CaboodleCreative